import { FormEvent, useState } from 'react';
import { API_BASE, ApiError, UsageResponse, validateKey } from '../api';

const DEMO_NAME = import.meta.env.VITE_DEMO_NAME || 'Property Stack';

/**
 * The home page, and the only thing an unauthenticated visitor can reach.
 *
 * It deliberately shows no search box, no sample results and no listing form —
 * per the brief, and because a demo that renders its own results before anyone
 * has a key teaches a prospect that the key does not matter.
 *
 * Validation is a real call to GET /v1/usage with the pasted key. Nothing is
 * checked client-side beyond "not empty": a format check here would be a second,
 * divergent copy of a rule the service already owns, and it would reject a
 * perfectly good key the day the format changes.
 */
export default function KeyGate({ onSignIn }: { onSignIn: (key: string, usage: UsageResponse) => void }) {
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [reveal, setReveal] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = key.trim();
    if (!trimmed) return;

    setChecking(true);
    setError(null);
    try {
      const usage = await validateKey(trimmed);
      onSignIn(trimmed, usage);
    } catch (err) {
      setError(err instanceof ApiError ? err.friendly : 'Something went wrong. Please try again.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">
          {DEMO_NAME} <small>Search API demo</small>
        </div>
      </header>

      <main className="page narrow">
        <h1>Enter your demo key</h1>
        <p className="lede">
          We have loaded a private index with sample listings for your market. The key we sent you
          unlocks it — and only it.
        </p>

        <form className="card" onSubmit={submit}>
          {error && <div className="note error">{error}</div>}

          <div className="field">
            <label htmlFor="key">
              Demo API key
              <span className="hint">Starts with <code>psk_</code>. Paste the whole thing, including the prefix.</span>
            </label>
            <div className="input-with-action">
              <input
                id="key"
                className="mono"
                type={reveal ? 'text' : 'password'}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="psk_…"
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                autoFocus
              />
              <button
                type="button"
                className="ghost"
                onClick={() => setReveal((r) => !r)}
                aria-label={reveal ? 'Hide key' : 'Show key'}
              >
                {reveal ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button type="submit" disabled={checking || !key.trim()}>
            {checking ? <><span className="spinner" /> Checking…</> : 'Open the demo'}
          </button>

          <p className="footnote">
            The key is checked against{' '}
            <code>{API_BASE ? `${API_BASE}/v1/usage` : '/v1/usage'}</code> and then kept in this
            browser only, so you stay signed in. Sign out to remove it.
          </p>
        </form>

        <div className="card">
          <h3>What you can do here</h3>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: 'var(--text-dim)' }}>
            <li>Search the sample index in plain language and see how the query was parsed.</li>
            <li>Add a listing by address and watch it get geocoded, enriched and indexed.</li>
            <li>Copy the exact curl, Python, Node or PHP call behind each action.</li>
          </ul>
          <p className="footnote">
            No key? Reply to the email that brought you here and we will issue one.
          </p>
        </div>
      </main>
    </div>
  );
}
